import { lazy, Suspense, useCallback, useEffect, useMemo, useState, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "wouter";
import { useGetCurrentUser } from "@workspace/api-client-react";
import { useAuth } from "@clerk/react";
import { useWebSignOut } from "@/hooks/use-web-sign-out";
import { PlateOSLogo } from "@/components/plate-os-logo";
import drawerPullLogo from "@/assets/plate-os-drawer-pull.png";
import { NotificationBell } from "@/components/notification-bell";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ThemePicker } from "@/components/layout/sidebar";
import { AdminModeControl } from "@/components/delegation-controls";
import { NAV_GROUPS, PRO_HREFS, findGroupId, isProUser } from "@/components/layout/nav-config";
import { useGroceryStore } from "@/lib/grocery-store";
import { LowerWorkspaceDialogProvider } from "@/lib/lower-workspace-dialog";
import { Lock, Sparkles, Shield, Crown, LogOut, LayoutDashboard, ListOrdered, LayoutGrid, type LucideIcon } from "lucide-react";

const PlateTalkConversation = lazy(() => import("./plate-talk"));

interface PlateTalkShellProps {
  children?: ReactNode;
  directoryBanner?: ReactNode;
  recoveryNotice?: ReactNode;
  ownerId?: string;
  conversationEnabled?: boolean;
}

type DirectoryItem = {
  href: string;
  tLabel: string;
  tDesc: string;
  icon: LucideIcon;
};

type DirectoryGroup = {
  id: string;
  tLabel: string;
  tDesc?: string;
  items: DirectoryItem[];
};

function getRouteGroupId(location: string): string {
  return (
    findGroupId(location) ??
    NAV_GROUPS.find((group) =>
      group.items.some((item) => location.startsWith(`${item.href}/`)),
    )?.id ??
    (location.startsWith("/admin")
      ? "admin"
      : location.startsWith("/coach")
        ? "coach"
        : "everyday")
  );
}

export default function PlateTalkShell({
  children,
  directoryBanner,
  recoveryNotice,
  ownerId,
  conversationEnabled = true,
}: PlateTalkShellProps) {
  const { t } = useTranslation();
  const { data: user } = useGetCurrentUser();
  const { isSignedIn } = useAuth();
  const { handleSignOut, isSigningOut, signOutError } = useWebSignOut();
  const [location, setLocation] = useLocation();
  const grocery = useGroceryStore();
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const shellRef = useRef<HTMLDivElement>(null);
  const collapsedDrawerRef = useRef<HTMLDivElement>(null);
  const moreTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isDrawerOpen) return;
    const activeElement = document.activeElement;
    const opener = activeElement instanceof HTMLElement && shellRef.current?.contains(activeElement)
      ? activeElement
      : moreTriggerRef.current;
    const dismissDirectory = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      // Nested tools/dialogs own their first Escape; the directory is a
      // non-modal disclosure and must not dismiss them or steal their focus.
      if (event.target instanceof Element && event.target.closest('[role="dialog"], [role="alertdialog"]')) return;
      event.preventDefault();
      setIsDrawerOpen(false);
      opener?.focus();
    };
    document.addEventListener("keydown", dismissDirectory);
    return () => document.removeEventListener("keydown", dismissDirectory);
  }, [isDrawerOpen]);

  const hasConversationAccess = conversationEnabled && isProUser(user);

  const roleGroups = useMemo<DirectoryGroup[]>(
    () => [
      ...(user?.role === "admin"
        ? [{
            id: "admin",
            tLabel: t("sidebar.admin", { defaultValue: "Admin" }),
            tDesc: "Health administration",
            items: [
              { href: "/admin", tLabel: t("sidebar.adminOverview", { defaultValue: "Overview" }), tDesc: "Health administration", icon: Shield },
              { href: "/admin/command-center", tLabel: "Command Center", tDesc: "Operations dashboard", icon: Shield },
              { href: "/admin/cto-reports", tLabel: "CTO Reports", tDesc: "Technical reports", icon: Shield },
              { href: "/admin/health-reports", tLabel: "Health Reports", tDesc: "Reviewed member reports", icon: Shield },
              { href: "/admin/cases", tLabel: "Cases & Incidents", tDesc: "Protected case operations", icon: Shield },
              { href: "/admin/users", tLabel: "User Performance", tDesc: "Member administration", icon: Shield },
              { href: "/admin/staff", tLabel: "Staff Directory", tDesc: "Staff access", icon: Shield },
              { href: "/admin/audience", tLabel: t("sidebar.adminAudience", { defaultValue: "Audience" }), tDesc: "Campaign operations", icon: Shield },
              { href: "/admin/audit", tLabel: "PHI Audit", tDesc: "Restricted-data audit", icon: Shield },
              { href: "/admin/access-audit", tLabel: "Access Changes", tDesc: "Account access history", icon: Shield },
              { href: "/admin/agents", tLabel: "Autonomous Agents", tDesc: "Agent operations", icon: Shield },
              { href: "/admin/system", tLabel: "System & Errors", tDesc: "System health", icon: Shield },
              { href: "/admin/build-info", tLabel: "Build Info", tDesc: "Release information", icon: Shield },
            ],
          }]
        : []),
      ...(user?.role === "coach"
        ? [{
            id: "coach",
            tLabel: t("sidebar.coachAi"),
            tDesc: "Member coaching dashboard",
            items: [{ href: "/coach/dashboard", tLabel: "Coach workspace", tDesc: "Member coaching dashboard", icon: Sparkles }],
          }]
        : []),
    ],
    [t, user?.role],
  );

  const dashboardGroups = useMemo<DirectoryGroup[]>(
    () => [
      ...NAV_GROUPS.map((group) => ({
        id: group.id,
        tLabel: group.tLabel,
        tDesc: group.tDesc,
        // Do not filter out /plate-talk anymore so it remains accessible in the drawer if it was in NAV_GROUPS
        items: group.items.map((item) => ({ ...item })),
      })),
      ...roleGroups,
    ],
    [roleGroups],
  );

  const routeCategory = getRouteGroupId(location);
  const [activeCategory, setActiveCategory] = useState<string>(routeCategory);
  const isHome = location === "/plate-talk" || location === "/";

  const activeGroup = dashboardGroups.find((group) => group.id === activeCategory) ?? dashboardGroups[0];
  const primaryDestinations = dashboardGroups
    .flatMap((group) => group.items)
    .filter((item) => ["/dashboard", "/plate-log", "/profile"].includes(item.href));

  useEffect(() => {
    const visualViewport = window.visualViewport;
    if (!visualViewport) return;
    const syncViewportHeight = () => {
      setViewportHeight(Math.round(visualViewport.height));
    };
    syncViewportHeight();
    visualViewport.addEventListener("resize", syncViewportHeight);
    visualViewport.addEventListener("scroll", syncViewportHeight);
    return () => {
      visualViewport.removeEventListener("resize", syncViewportHeight);
      visualViewport.removeEventListener("scroll", syncViewportHeight);
    };
  }, []);

  const [prevLocation, setPrevLocation] = useState(location);

  useEffect(() => {
    if (location !== prevLocation) {
      setPrevLocation(location);
      const available = dashboardGroups.some((group) => group.id === routeCategory);
      if (available && !isHome) {
        setActiveCategory(routeCategory);
      }
    }
  }, [location, prevLocation, dashboardGroups, routeCategory, isHome]);

  // Selecting feature collapses drawer
  useEffect(() => {
    if (!isHome) {
      setIsDrawerOpen(false);
    }
  }, [location, isHome]);

  // The feature surface scrolls behind the fixed drawer. Measure only the
   // collapsed handle/composer region so its clearance tracks wrapped text,
  // attachments, transcription, and narrow viewports without ever including
  // the expanded directory (which intentionally overlays the surface).
  useEffect(() => {
    const shell = shellRef.current;
    const collapsedDrawer = collapsedDrawerRef.current;
    if (!shell || !collapsedDrawer) return;

    const measure = () => {
       // The 76px pull ring protrudes 38px above the drawer lip. Leave another
       // 16px between it and the last interactive item in the feature surface.
       const height = Math.max(0, Math.ceil(collapsedDrawer.getBoundingClientRect().height)) + 38 + 16;
      shell.style.setProperty("--plate-talk-composer-clearance", `${height}px`);
    };

    // The expanded composer changes layout to expose its full toolbar. Keep
    // the last collapsed measurement while the directory is open so expanded
    // content remains an overlay rather than changing background scrolling.
    if (isDrawerOpen) return;

    measure();
    window.addEventListener("resize", measure);
    const ResizeObserverConstructor = window.ResizeObserver;
    const resizeObserver = ResizeObserverConstructor
      ? new ResizeObserverConstructor(measure)
      : null;
    resizeObserver?.observe(collapsedDrawer);

    return () => {
      window.removeEventListener("resize", measure);
      resizeObserver?.disconnect();
    };
  }, [isDrawerOpen]);

  // Drag tracking
  const dragStartY = useRef<number | null>(null);
  const draggedRef = useRef(false);
  const DRAG_THRESHOLD = 50;

  const handlePointerDown = (e: React.PointerEvent) => {
    dragStartY.current = e.clientY;
    draggedRef.current = false;
    if (typeof e.currentTarget.setPointerCapture === "function") {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (dragStartY.current === null) return;
    const deltaY = e.clientY - dragStartY.current;

    // Pull down to close
    if (isDrawerOpen && deltaY > DRAG_THRESHOLD) {
      setIsDrawerOpen(false);
      draggedRef.current = true;
      dragStartY.current = null;
    }
    // Pull up to open
    else if (!isDrawerOpen && deltaY < -DRAG_THRESHOLD) {
      setIsDrawerOpen(true);
      draggedRef.current = true;
      dragStartY.current = null;
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    dragStartY.current = null;
    if (
      typeof e.currentTarget.hasPointerCapture === "function" &&
      e.currentTarget.hasPointerCapture(e.pointerId)
    ) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const handlePointerCancel = (e: React.PointerEvent) => {
    dragStartY.current = null;
    draggedRef.current = false;
    if (
      typeof e.currentTarget.hasPointerCapture === "function" &&
      e.currentTarget.hasPointerCapture(e.pointerId)
    ) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const handleHandleClick = () => {
    // A threshold drag also emits a click in some browsers. Consume that
    // synthetic click so one gesture cannot open and immediately close again.
    if (draggedRef.current) {
      draggedRef.current = false;
      return;
    }
    setIsDrawerOpen((open) => !open);
  };

  const handleHandleKeyDown = (e: React.KeyboardEvent) => {
    // A keyboard activation has no pointer-up to clear the drag guard. Clear
    // it before the button's native keyboard click so keyboard use remains
    // reliable even when a browser suppresses the drag-generated click.
    if (e.key === "Enter" || e.key === " ") {
      draggedRef.current = false;
    }
  };

  const handleDirectoryNavigate = useCallback((href: string, event: React.MouseEvent) => {
    if (location === "/grocery" && grocery.isBusy && href !== "/grocery") {
      event.preventDefault();
      if (window.confirm(t("sidebar.groceryNavGuard"))) setLocation(href);
    }
  }, [grocery.isBusy, location, setLocation, t]);

  const shellStyle = viewportHeight
    ? { height: `${viewportHeight}px` }
    : undefined;

  return (
    <LowerWorkspaceDialogProvider>
      <div
        ref={shellRef}
        data-testid="plate-talk-page"
        className="fixed inset-0 flex flex-col overflow-hidden bg-background text-foreground"
        style={shellStyle}
      >
        <div className="ambient-bg" aria-hidden />
        {recoveryNotice}

        {/* Main Background Area */}
        <main className="relative min-h-0 flex-1 z-0 flex flex-col">
          {/* Plate Talk Conversation - always mounted to preserve state */}
          <section
            aria-label={t("sidebar.plateTalk")}
            data-testid="plate-talk-conversation-region"
            className={`absolute inset-0 flex flex-col bg-background ${!isHome ? "hidden pointer-events-none" : ""}`}
          >
            {!hasConversationAccess ? (
              <div data-testid="plate-talk-paywall" className="flex h-full w-full flex-col items-center justify-center overflow-y-auto bg-card/50 p-6 z-10 relative">
                <div data-testid="plate-talk-paywall-card" className="flex max-w-md flex-col items-center gap-4 rounded-2xl border border-border/60 bg-card p-6 text-center shadow-sm">
                  <div className="flex items-center gap-2 text-lg font-semibold text-foreground">
                    <Crown className="h-5 w-5 text-amber-400" />
                    {t("sidebar.plateTalk")}
                  </div>
                  <p className="text-sm text-muted-foreground">{t("sidebar.plateTalkDesc")}</p>
                  <Link
                    href="/subscription"
                    className="flex min-h-11 w-full items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                  >
                    {t("sidebar.subscription")}
                  </Link>
                </div>
              </div>
            ) : (
              <Suspense fallback={
                <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                  <div className="flex flex-col items-center gap-2">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary/40 border-t-primary" />
                    <span className="text-sm">Loading Plate Talk...</span>
                  </div>
                </div>
              }>
                <PlateTalkConversation ownerId={ownerId} isAdmin={user?.role === "admin"} conversationEnabled={hasConversationAccess} />
              </Suspense>
            )}
          </section>

          {/* Feature Window Full Background */}
           {!isHome && children && (
            <section
               data-testid="plate-talk-feature-scroll"
               className="absolute inset-0 flex flex-col overflow-y-auto bg-background z-10"
               style={{ scrollPaddingBottom: "var(--plate-talk-composer-clearance, 174px)" }}
            >
              {children}
               {/* An in-flow spacer, rather than padding on the scrollport, keeps
                   the last row scrollable above the fixed drawer in every browser. */}
               <div
                 aria-hidden="true"
                 data-testid="plate-talk-feature-clearance"
                 className="shrink-0"
                 style={{ height: "var(--plate-talk-composer-clearance, 174px)" }}
               />
            </section>
          )}
        </main>

        {/* Windows opened by the retained conversation must remain above a
            feature route even while that conversation section is hidden. */}
        <div
          id="plate-talk-shell-overlay"
          data-testid="plate-talk-shell-overlay"
          className="pointer-events-none absolute inset-0 z-[60]"
        />

        {/* Bottom Drawer */}
        <div 
          className="fixed inset-x-0 bottom-0 z-50 pointer-events-none flex flex-col justify-end px-2 md:px-0"
        >
          {/* We use a wrapper with transition to slide it up and down */}
          <div 
            className="plate-talk-drawer pointer-events-auto backdrop-blur-3xl flex flex-col w-full max-w-6xl mx-auto max-h-[calc(100dvh_-_38px)] md:max-h-[78vh] relative"
            data-testid="drawer-container"
            data-expanded={isDrawerOpen}
          >
            {/* Polished silver highlight on top lip */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-foreground/20 to-transparent rounded-t-[2.5rem] pointer-events-none" />

            <div ref={collapsedDrawerRef} className="relative pt-3">
              {/* Open metallic docking ring; the logo itself remains transparent. */}
              <div data-testid="drawer-handle-boundary" className="plate-talk-handle-ring absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 w-[76px] h-[76px] rounded-full bg-transparent pointer-events-none" />

              {/* Drag Handle */}
              <button 
                className="plate-talk-chrome-focus group absolute left-1/2 top-0 z-10 flex h-[72px] w-[72px] -translate-x-1/2 -translate-y-1/2 cursor-grab touch-none items-center justify-center rounded-full bg-transparent p-0 transition-all active:cursor-grabbing"
                onClick={handleHandleClick}
                onKeyDown={handleHandleKeyDown}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerCancel}
                data-testid="drawer-handle"
                aria-expanded={isDrawerOpen}
                aria-label={isDrawerOpen ? "Pull down to close Plate OS controls" : "Pull up to open Plate OS controls"}
              >
                <img
                  src={drawerPullLogo}
                  alt=""
                  aria-hidden="true"
                  data-testid="drawer-pull-logo"
                  className={`h-16 w-16 object-contain transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] ${
                    isDrawerOpen ? "" : "group-hover:scale-105 group-active:scale-95"
                  }`}
                  style={{
                    transform: isDrawerOpen ? "rotate(180deg)" : undefined,
                    filter: "drop-shadow(0 6px 10px rgba(0,0,0,0.6))"
                  }}
                />
                <span className="sr-only">
                  {isDrawerOpen ? "Pull down" : "Pull up"}
                </span>
              </button>

              {/* Composer Slot (Always visible) */}
              <div className="flex-none px-3 md:px-6 pb-4">
                <div id="plate-talk-composer-slot" />
                <nav aria-label={t("sidebar.groups.everyday")} className="mt-2 grid grid-cols-4 gap-1 rounded-xl border border-foreground/10 bg-background/40 p-1" data-testid="member-shortcuts">
                  {[
                    { href: "/plate-talk", label: t("sidebar.plateTalk"), icon: Sparkles },
                    { href: "/dashboard", label: t("common.today"), icon: LayoutDashboard },
                    { href: "/plate-log", label: t("sidebar.plateLog"), icon: ListOrdered },
                  ].map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      aria-current={location === item.href ? "page" : undefined}
                      onClick={(event) => handleDirectoryNavigate(item.href, event)}
                      className={`plate-talk-chrome-focus flex min-h-[44px] min-w-0 items-center justify-center gap-1 rounded-lg px-1 text-xs font-semibold transition-colors ${
                        location === item.href ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
                      }`}
                    >
                      <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  ))}
                  <button
                    ref={moreTriggerRef}
                    type="button"
                    aria-label={t("sidebar.more")}
                    aria-expanded={isDrawerOpen}
                    aria-controls="plate-talk-navigation-directory"
                    onClick={() => setIsDrawerOpen(true)}
                    className={`plate-talk-chrome-focus flex min-h-[44px] min-w-0 items-center justify-center gap-1 rounded-lg px-1 text-xs font-semibold transition-colors ${
                      isDrawerOpen ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
                    }`}
                  >
                    <LayoutGrid className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <span className="truncate">{t("sidebar.more")}</span>
                  </button>
                </nav>
              </div>
            </div>

            {/* Expanded Drawer Content */}
            <div 
              id="plate-talk-navigation-directory"
              role="region"
              aria-label={t("sidebar.more")}
              className={`overflow-y-auto transition-all duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${isDrawerOpen ? 'opacity-100' : 'opacity-0 hidden'}`}
              style={{ height: isDrawerOpen ? '60vh' : '0px' }}
              data-testid="drawer-content"
            >
              <div className="px-4 md:px-6 space-y-6 pb-12">
                {/* Header Row */}
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <Link href="/plate-talk" className="flex items-center gap-2 focus-visible:outline-none rounded-md" onClick={() => setIsDrawerOpen(false)}>
                    <PlateOSLogo size={24} />
                    <span className="text-sm font-bold uppercase tracking-[0.16em]">Plate OS</span>
                  </Link>
                  <div className="flex items-center gap-2 flex-wrap">
                    <ThemePicker compact />
                    <NotificationBell />
                    {user?.role === "admin" && <AdminModeControl />}
                    {user && (
                      <Link
                        href="/profile"
                        aria-label={t("sidebar.profileLabel")}
                        data-testid="link-profile-tools"
                        className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        onClick={() => setIsDrawerOpen(false)}
                      >
                        <Avatar className="h-9 w-9 border border-foreground/15">
                          <AvatarImage src={user.avatarUrl || undefined} />
                          <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">
                            {(user.firstName?.trim()?.[0] ?? "•").toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      </Link>
                    )}
                  </div>
                </div>

                {user && isSignedIn && (
                  <div className="flex flex-col items-start gap-1">
                    <button
                      type="button"
                      onClick={() => void handleSignOut()}
                      disabled={isSigningOut}
                      className="plate-talk-chrome-focus inline-flex min-h-11 items-center gap-2 rounded-xl border border-foreground/10 px-4 text-sm font-medium text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground disabled:cursor-wait disabled:opacity-60"
                    >
                      <LogOut className="h-4 w-4" aria-hidden="true" />
                      {t("sidebar.signOut")}
                    </button>
                    {signOutError && <p role="alert" className="text-sm text-destructive">{t("common.error")}. {t("common.retry")}</p>}
                  </div>
                )}

                <nav
                  aria-label={t("sidebar.groups.everyday")}
                  data-testid="plate-talk-primary-nav"
                  className="grid grid-cols-3 gap-2"
                >
                  {primaryDestinations.map((item) => {
                    const isActive = location === item.href || location.startsWith(`${item.href}/`);
                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        aria-current={isActive ? "page" : undefined}
                        data-testid={`primary-nav-${item.href.slice(1)}`}
                        onClick={(event) => {
                          handleDirectoryNavigate(item.href, event);
                          if (!event.defaultPrevented) setIsDrawerOpen(false);
                        }}
                        className={`plate-talk-chrome-focus flex min-h-11 items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${
                          isActive
                            ? "border-foreground/20 bg-foreground/10 text-foreground"
                            : "border-foreground/10 bg-foreground/[0.03] text-muted-foreground hover:bg-foreground/[0.07] hover:text-foreground"
                        }`}
                      >
                        <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                        <span className="truncate">{item.tLabel.includes(".") ? t(item.tLabel) : item.tLabel}</span>
                      </Link>
                    );
                  })}
                </nav>

                {/* Category Tabs */}
                <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-2 border-b border-foreground/10" role="tablist" aria-label={t("sidebar.groups.everyday")}>
                  {dashboardGroups.map(group => (
                    <button 
                      key={group.id}
                      role="tab"
                      aria-selected={activeCategory === group.id}
                      onClick={() => setActiveCategory(group.id)}
                      className={`plate-talk-chrome-focus min-h-[44px] px-4 py-1.5 rounded-full text-[13px] font-semibold whitespace-nowrap transition-all duration-200 ${
                        activeCategory === group.id 
                          ? 'bg-foreground/10 text-foreground shadow-[inset_0_1px_1px_hsl(var(--foreground)/0.12),0_1px_2px_rgba(0,0,0,0.2)]'
                          : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground'
                      }`}
                    >
                      {group.tLabel.includes(".") ? t(group.tLabel) : group.tLabel}
                    </button>
                  ))}
                </div>

                {/* Directory Icons Grid */}
                <div className="grid grid-cols-3 min-[420px]:grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3" data-testid="plate-talk-directory">
                  {activeGroup?.items.map(item => {
                    const isActive = location.startsWith(item.href) && item.href !== "/";
                    return (
                      <Link 
                        href={item.href} 
                        key={item.href}
                        onClick={(e) => {
                           handleDirectoryNavigate(item.href, e);
                           if (!e.defaultPrevented) setIsDrawerOpen(false);
                        }}
                        className="plate-talk-chrome-focus flex flex-col items-center gap-2 p-2 rounded-xl hover:bg-foreground/5 border border-transparent hover:border-foreground/10 transition-all duration-200 group"
                      >
                        <div className={`relative w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                          isActive 
                            ? 'bg-gradient-to-b from-card/80 to-background border border-foreground/15 shadow-[inset_0_1px_1px_hsl(var(--foreground)/0.18),0_4px_12px_rgba(0,0,0,0.32)] text-foreground'
                            : 'bg-foreground/5 border border-transparent shadow-[inset_0_2px_4px_rgba(0,0,0,0.3)] text-muted-foreground group-hover:text-foreground group-hover:shadow-[inset_0_1px_2px_rgba(0,0,0,0.2)]'
                        }`}>
                          <item.icon className={`w-6 h-6 transition-transform duration-300 ${isActive ? 'scale-110 drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]' : 'group-hover:scale-110'}`} />
                          {!isProUser(user) && PRO_HREFS.has(item.href) && (
                            <div className="absolute -bottom-1 -right-1 bg-card rounded-full p-0.5 shadow-sm border border-foreground/10">
                              <Lock className="w-3 h-3 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                        <span className={`text-[10px] font-semibold text-center leading-tight max-w-full transition-colors duration-200 ${
                          isActive ? 'text-foreground' : 'text-muted-foreground group-hover:text-foreground'
                        }`}>
                          {item.tLabel.includes(".") ? t(item.tLabel) : item.tLabel}
                        </span>
                      </Link>
                    );
                  })}
                </div>
                
                {directoryBanner}
                
              </div>
            </div>
          </div>
        </div>
      </div>
    </LowerWorkspaceDialogProvider>
  );
}
